import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { useDispatch } from 'react-redux';
import { Permission } from 'zl-shared/dist/auth';

import { hasPermission } from '@/components/Authorize';
import HighlightsList from '@/components/HighlightsList';
import { HighlightsPlaylistListRef } from '@/components/HighlightsList/HighlightsPlaylistList';
import { HighlightsVideoListRef } from '@/components/HighlightsList/HighlightsVideoList';
import { useFormulaFilter } from '@/components/HighlightsList/hooks';
import { HighlightType } from '@/components/HighlightsList/types';
import { toHighlightType } from '@/components/HighlightsList/utils';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import { setMembersOnlyWarningModalState } from '@/components/MembersOnlyWarningModal/redux/actions';
import NotificationPermissionModal from '@/components/NotificationPermissionModal';
import { setProductsPromptModalState } from '@/components/products/ProductsPromptModal/redux/actions';
import ShareModal from '@/components/ShareModal/Modal';
import { setQueryParams } from '@/hooks/route';
import useOnMount from '@/hooks/useOnMount';
import { Video, VideoV2 } from '@/queries/media/types';
import { PlaylistListItem } from '@/queries/playlists/types';
import { useSelector, useStore } from '@/redux/helper';
import { selectHasExplorerFeature } from '@/redux/selectors/users';
import { selectIsGuestSessionLocked } from '@/redux/slices/deviceSettings/selectors';
import MobileMap from '@/routes/map/Mobile';
import { trackMP } from '@/shared/mp';

import HomeVideoSection, { MiniPlayerContentMode } from './HomeVideoSection';
import { useHabitatForStream, useIsAllHabitatsOffline } from './hooks';
import HomeHighlightVideoInfoBar from './InfoBar/HomeHighlightVideoInfoBar';
import HomeHighlightVideoWithPlaylistInfoBar from './InfoBar/HomeHighlightVideoWithPlaylistInfoBar';
import HomeLiveStreamInfoBar from './InfoBar/HomeLiveStreamInfoBar';

const HomeContent = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const isMobile = useIsMobileLayout();
  const ref = useRef<HTMLDivElement>(null);
  const [{ matches }] = useRouter();
  const { mediaId, playlistId, type } = matches ?? {};

  const highlightType = toHighlightType(type?.toLowerCase(), hasPermission(Permission.Habitat.UploadMedia));

  const isGuestSessionLocked = useSelector((state) => selectIsGuestSessionLocked(state, null));
  const [isAmbientMode, setIsAmbientMode] = useState(false);
  const [preferMuted, setPreferMuted] = useState(() => store.getState().settings.videoPlayerConfig.muted);
  const [miniPlayerContentMode, setMiniPlayerContentMode] = useState<MiniPlayerContentMode>('highlight');
  const [currentVideo, setCurrentVideo] = useState<Video | VideoV2 | undefined>();

  const { selectedFormulas, setSelectedFormulas, formulaHighlightOverride } = useFormulaFilter();

  const [shouldUsePopup, setShouldUsePopup] = useState(!!mediaId);
  const [isPlayingList, setIsPlayingList] = useState(false);
  const [isPlaylistMode, setIsPlaylistMode] = useState(!!playlistId);

  const highlightsListRef = useRef<HighlightsVideoListRef>(null);
  const playlistListRef = useRef<HighlightsPlaylistListRef>(null);

  const isAllHabitatsOffline = useIsAllHabitatsOffline();
  const hasExplorerFeature = useSelector(selectHasExplorerFeature);
  const isLoggedIn = useSelector((state) => state.user.logged);
  const habitatForStream = useHabitatForStream({
    keepPreferredHabitatId: isLoggedIn ? !hasExplorerFeature || !isAmbientMode : false,
    restrictToFreemiumHabitats: false,
  });

  const closeMiniPlayer = () => {
    setCurrentVideo(undefined);
    highlightsListRef.current?.stop();
    playlistListRef.current?.stop();
    setIsPlayingList(false);
    setQueryParams({ mediaId: undefined }, true);
  };

  const onNextVideo = () => {
    highlightsListRef.current?.next();
    playlistListRef.current?.next();
  };

  const onVideoChange = (video: Video | VideoV2) => {
    setCurrentVideo(video);
    setQueryParams({ mediaId: video?._id }, true);
    setShouldUsePopup(false);
    if (!isMobile) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const onVideoClick = (highlight: Video | VideoV2, index: number) => {
    setMiniPlayerContentMode('live-stream');
    setIsPlayingList(true);
    trackMP('home-highlight-content-clicked', { indexInList: index, highlight });
  };

  const onPlaylistChange = (playlist: PlaylistListItem) => {
    setQueryParams({ playlistId: playlist._id, mediaId: undefined }, true);
  };

  const onPlaylistClick = (playlist: PlaylistListItem) => {
    setMiniPlayerContentMode('live-stream');
    setIsPlaylistMode(true);
    setIsPlayingList(true);
    trackMP('playlist-selected', {
      playlistId: playlist._id,
      entryPoint: 'home-page',
    });
  };

  const onSwitchContentMode = () => {
    const newMode = miniPlayerContentMode === 'highlight' ? 'live-stream' : 'highlight';
    if (newMode === 'highlight') {
      // close the mini player if we want to minimize the highlight video player
      closeMiniPlayer();
    }
    setMiniPlayerContentMode(newMode);
    trackMP('home-switched-miniplayer-mode', {
      miniPlayerCurrentContentMode: miniPlayerContentMode,
      miniPlayerNewContentMode: newMode,
    });
  };

  const onLoadMore = () => {
    trackMP('home-fetch-more-clicked', {
      miniPlayerCurrentContentMode: miniPlayerContentMode,
      habitatId: habitatForStream?._id,
      habitatName: habitatForStream?.title,
      zooName: habitatForStream?.zoo.name,
    });
  };

  const onCloseMiniPlayer = () => {
    closeMiniPlayer();
    trackMP('home-mini-player-closed', { currentHighlight: currentVideo });
  };

  const onChangeHighlightType = (type: HighlightType) => {
    if (type !== highlightType) {
      setQueryParams({ type, playlistId: undefined, mediaId: undefined }, true);
      setCurrentVideo(undefined);
    }
  };

  const onUpgradeClick = useCallback(() => {
    if (isLoggedIn) {
      dispatch(setProductsPromptModalState({ primaryProductType: 'explorer-pass', entryPoint: 'home-upgrade-click' }));
    } else {
      dispatch(setMembersOnlyWarningModalState({ type: null }));
    }
  }, [dispatch, isLoggedIn]);

  const onAmbientModeChange = useCallback(
    (newValue: boolean) => {
      if (!hasExplorerFeature) {
        if (!isLoggedIn) {
          dispatch(setMembersOnlyWarningModalState({ type: 'ambient-mode' }));
        } else {
          dispatch(
            setProductsPromptModalState({
              primaryProductType: 'explorer-pass',
              entryPoint: 'ambient-mode',
            }),
          );
        }
        trackMP('click-ambient-mode-button', { fromEnabled: false, toEnabled: false });
        return;
      }
      setIsAmbientMode(newValue);
      trackMP('click-ambient-mode-button', { fromEnabled: !newValue, toEnabled: newValue });
    },
    [hasExplorerFeature, isLoggedIn, dispatch],
  );

  // set highlight to first video if all habitats are offline
  useEffect(() => {
    if (isAllHabitatsOffline && !mediaId) {
      setPreferMuted(true);
      setMiniPlayerContentMode('live-stream');
      setIsPlayingList(true);
    }
  }, [isAllHabitatsOffline, mediaId]);

  useOnMount(() => {
    trackMP('home-visit', {
      currentHabitatId: habitatForStream?._id,
      currentHabitatInfo: habitatForStream,
      areAllHabitatsOffline: isAllHabitatsOffline,
    });
  });

  return (
    <div ref={ref} className="mobile:flex mobile:h-full mobile:flex-col mobile:overflow-hidden mobile:bg-white">
      <div className="top-0 z-10 mobile:shrink-0">
        <HomeVideoSection
          highlight={currentVideo}
          habitat={habitatForStream}
          miniPlayerContentMode={miniPlayerContentMode}
          onNextHighlight={onNextVideo}
          onSwitchContentMode={onSwitchContentMode}
          onCloseMiniPlayer={onCloseMiniPlayer}
          preferMuted={preferMuted}
          onMuteChange={setPreferMuted}
          isShareModalOpen={!!(shouldUsePopup && mediaId)}
          isAmbientMode={isAmbientMode}
          onAmbientModeChange={onAmbientModeChange}
        />
        {miniPlayerContentMode === 'highlight'
          ? habitatForStream && <HomeLiveStreamInfoBar habitat={habitatForStream} />
          : currentVideo &&
            (isPlaylistMode && playlistId ? (
              <HomeHighlightVideoWithPlaylistInfoBar
                video={currentVideo}
                playlistId={playlistId}
                onNextVideo={onNextVideo}
                onBackToAllPlaylists={() => {
                  setIsPlaylistMode(false);
                  if (playlistId && !playlistListRef.current?.hasPlaylist(playlistId)) {
                    setQueryParams({ playlistId: undefined }, true);
                  }
                }}
              />
            ) : (
              <HomeHighlightVideoInfoBar highlight={currentVideo} onNextHighlight={onNextVideo} />
            ))}
      </div>
      <div className="relative mobile:grow mobile:overflow-y-auto mobile:bg-secondary-green desktop:min-h-[50svh] desktop:pb-3">
        {isMobile ? (
          <>
            <MobileMap />
            <NotificationPermissionModal />
          </>
        ) : !hasExplorerFeature || isGuestSessionLocked ? (
          <HighlightsList.VideoList
            key={`freemium-clip-${formulaHighlightOverride}`}
            ref={highlightsListRef}
            play={isPlayingList}
            type="clip"
            formulaHighlightOverride={formulaHighlightOverride}
            onVideoChange={onVideoChange}
            onVideoClick={onVideoClick}
            onUpgradeClick={onUpgradeClick}
            pageSize={24}
            className="px-4 pt-3.5"
          />
        ) : (
          <>
            <HighlightsList.Header
              type={highlightType}
              onTypeChange={onChangeHighlightType}
              selectedFormulas={selectedFormulas}
              onFormulaChange={setSelectedFormulas}
              className="sticky top-0 z-[1] px-4"
            />

            {highlightType === 'highlight' ? (
              <HighlightsList.PlaylistList
                key={highlightType}
                type="highlight"
                ref={playlistListRef}
                play={isPlayingList}
                playlistIdFromURL={playlistId}
                videoIdFromURL={mediaId}
                isExpanded={isPlaylistMode}
                onVideoChange={onVideoChange}
                onVideoClick={onVideoClick}
                onPlaylistChange={onPlaylistChange}
                onPlaylistClick={onPlaylistClick}
                className="flex min-h-full flex-col px-4"
              />
            ) : highlightType === 'behavior' ? (
              <HighlightsList.PlaylistList
                key={highlightType}
                type="behavior"
                ref={playlistListRef}
                play={isPlayingList}
                playlistIdFromURL={playlistId}
                videoIdFromURL={mediaId}
                isExpanded={isPlaylistMode}
                onVideoChange={onVideoChange}
                onVideoClick={onVideoClick}
                onPlaylistChange={onPlaylistChange}
                onPlaylistClick={onPlaylistClick}
                className="flex min-h-full flex-col px-4"
              />
            ) : (
              <HighlightsList.VideoList
                key={`${highlightType}-${formulaHighlightOverride}`}
                ref={highlightsListRef}
                play={isPlayingList}
                type={highlightType}
                formulaHighlightOverride={highlightType === 'clip' ? formulaHighlightOverride : undefined}
                onVideoChange={onVideoChange}
                onVideoClick={onVideoClick}
                onLoadMore={onLoadMore}
                className="px-4"
              />
            )}
          </>
        )}
      </div>

      {isGuestSessionLocked ? null : shouldUsePopup && mediaId ? (
        <ShareModal
          open
          shouldLoadPubnub
          mediaId={mediaId}
          isDrawer={isMobile}
          onClose={() => {
            setCurrentVideo(undefined);
            setShouldUsePopup(false);
            if (isMobile) {
              setQueryParams({ mediaId: undefined, playlistId: undefined, type: undefined }, true);
            } else {
              setQueryParams({ mediaId: undefined }, true);
            }
          }}
        />
      ) : null}
    </div>
  );
};

export default HomeContent;
